import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { ThemeToggle } from './ThemeToggle';
import { TextScaleControl } from './TextScaleControl';
import { getSiteSettings } from '../services/api';
import { isAuthenticated } from '../utils/auth';

export const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getSiteSettings();
        setSettings(data);
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    const checkAuth = () => {
      setIsAdminLoggedIn(isAuthenticated());
    };
    checkAuth();
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  const navLinks = settings?.nav_items || [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/listings', label: 'Listings' },
    { path: '/qualifications', label: 'Qualifications' }
  ];

  const isActive = (path) => location.pathname === path;

  // Calculate header height based on logo size
  const logoHeight = settings?.logo_image ? (settings?.logo_height || 48) : 48;
  const headerHeight = Math.max(64, logoHeight + 16); // Minimum 64px, or logo height + padding

  return (
    <header className={`sticky z-50 w-full border-b bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800 ${isAdminLoggedIn ? 'top-[50px]' : 'top-0'}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between" style={{ minHeight: `${headerHeight}px`, paddingTop: '8px', paddingBottom: '8px' }}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 transition-transform hover:scale-105">
            {settings?.logo_image ? (
              <>
                <img 
                  src={settings.logo_image} 
                  alt={settings?.logo_text || 'Logo'} 
                  className="object-contain"
                  style={{ 
                    height: `${settings?.logo_height || 48}px`,
                    width: settings?.logo_width ? `${settings.logo_width}px` : 'auto'
                  }}
                />
                {settings?.show_logo_text && (
                  <div className="hidden lg:flex flex-col">
                    <span className="text-xl font-bold text-otter-green dark:text-white">
                      {settings?.logo_text || 'Otter River Properties'}
                    </span>
                    {settings?.tagline && (
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {settings.tagline}
                      </span>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-otter-green">
                  <Home className="h-6 w-6 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    {settings?.logo_text || 'Otter River Properties'}
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {settings?.tagline || 'Lynchburg, VA'}
                  </span>
                </div>
              </>
            )}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              // Handle external links
              if (link.external) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-md text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white"
                  >
                    {link.label}
                  </a>
                );
              }
              // Handle internal links
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-otter-green-50 text-otter-green-800 dark:bg-otter-green-800/30 dark:text-otter-green-300'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <TextScaleControl />
            <ThemeToggle />
            <Link to="/contact">
              <Button className="ml-2 bg-otter-green hover:bg-otter-green-600 text-white">
                Contact Us
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button and Theme Toggle */}
          <div className="md:hidden flex items-center gap-2">
            <TextScaleControl />
            <ThemeToggle />
            <button
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 space-y-2 animate-in fade-in slide-in-from-top-2">
            {navLinks.map((link) => {
              // Handle external links
              if (link.external) {
                return (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 rounded-md text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                );
              }
              // Handle internal links
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-otter-green-50 text-otter-green-800 dark:bg-otter-green-800/30 dark:text-otter-green-300'
                      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              );
            })}
            <Link to="/contact" className="w-full">
              <Button className="w-full bg-otter-green hover:bg-otter-green-600 text-white">
                Contact Us
              </Button>
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
};